/**
 * water_body — ONE description of WHICH body of water a point or a view occupies (shader gaps R1, 2026-09-29; the
 * report's §4.6 / §14.8: "a mountain lake must not use the global sea level").
 *
 * `bodyAt(x, z)` → { kind: 'sea' | 'lake' | 'river', id, level, surfaceAt, optics, turbidity, flow } built from the
 * sources the world already has — the sea level + its shoreline (AtlasWaterModule), the baked lake regions
 * (lib/lakes `lakeSurfaceAtBaked`, labelled into lakes here), the river network (@shared/terrain `riverWaterAt` and its
 * segment table). The optics are lib/water_look's `OPTICS_BY_BODY` (the body plumbing this extends — no parallel
 * configuration layer). Consumers:
 *   - the GROUND under a lake or a river (lib/water_bed): a packed uniform table of the lakes (a 64² id mask over the
 *     lake region + one slot per lake: level, turbidity) and the nearest river segments (endpoints, water levels, half
 *     widths) — `packBodyTable`; `decodeBodyTable` is the shader's arithmetic on the CPU, the harness's oracle;
 *   - the WATERLINE (AtlasWaterModule): the near-plane line where the displaced surface cuts the lens (engine
 *     waterLook.nearPlaneWaterline, fed by `waterlineInputFor` from a camera);
 *   - the MEDIUM, the overlay, the fog and the shafts under a lake or a river (their optics).
 * Pure apart from the registered sources; no three.js at runtime.
 *
 * THE CARRIER CHOICE (measured, not assumed): the ground's sampler budget. The v2 ground binds 2 texture arrays + the
 * macro map (+ the rain-shelter map, the cloud-shadow map), and three adds the sky PMREM, one depth texture per CSM
 * cascade (up to 4) and, on high+, a cookie map per street-lamp spot light: a lake-level texture would be the ~17th
 * sampled texture on a WebGPU device whose default limit is 16 — an invalid pipeline is a black ground. A uniform
 * table costs no sampler: 706 vec4 (11.0 KB — under WebGL2's 16 KB uniform-block floor; WebGPU's default is 64 KB) in ONE uniform buffer binding.
 */
import type { LakeRegion } from './lakes.js';
import { LAKE_GRID_N, LAKE_REGION_SIZE } from './lakes.js';
import { riverNetworkTable, riverWaterAt } from '@shared/terrain.js';
import { OPTICS_BY_BODY, type BodyOptics } from './water_look.js';
import { manningSpeed } from './river_geometry.js'; // the ribbon's own speed rule (one Manning, not two)
import { nearPlaneWaterline, type NearPlaneWaterline } from 'enginex/environment/water/waterLook.js';

export type WaterBodyKind = 'sea' | 'lake' | 'river';

export interface WaterBody {
  kind: WaterBodyKind;
  /** 'sea' | 'lake:<level dm>:<cx>:<cz>' | 'river:<segment index>'. */
  id: string;
  /** The still water level (m). */
  level: number;
  /** The drawn surface over (x, z) — the sea's waves where the water module registered them, else the level. */
  surfaceAt(x: number, z: number): number;
  optics: BodyOptics;
  /** The constituents' share (1 = the kind's reference water; lakes vary 0.85–1.35 by id, ponds murkier). */
  turbidity: number;
  /** Downstream direction (unit xz) and speed (m/s); 0 for the sea and the lakes. */
  flow: { x: number; z: number; speed: number };
}

/** Where each body's truth comes from. The water module registers the live ones (`setWaterBodySources`). */
export interface WaterBodySources {
  seed: string;
  seaLevel: number;
  /** True over baked sea (the shoreline field says water). */
  seaAt(x: number, z: number): boolean;
  /** The drawn sea surface (waves) over (x, z); default the sea level. */
  seaSurfaceAt?(x: number, z: number): number;
  /** The baked lake level over (x, z) or null — default `lakeSurfaceAtBaked`'s answer is passed in by the owner. */
  lakeAt(x: number, z: number): number | null;
  /** The river water level over (x, z) or null — default `riverWaterAt(seed, …)`. */
  riverAt?(x: number, z: number): number | null;
}

let sources: WaterBodySources | null = null;
/** The water module registers the live sources (null on teardown). */
export function setWaterBodySources(s: WaterBodySources | null): void { sources = s; }
export function waterBodySources(): WaterBodySources | null { return sources; }

// ── Lakes: the region's wet nodes labelled into lakes ───────────────────────

export interface LakeInfo {
  /** Index into `labels.lakes` (the node label). */
  index: number;
  level: number;
  nodes: number;
  /** Centroid and bounds (absolute m). */
  cx: number; cz: number; x0: number; z0: number; x1: number; z1: number;
  id: string;
  turbidity: number;
}

export interface LakeLabels {
  region: LakeRegion;
  /** Per node: the lake index, −1 dry. Row-major like the region (row = z). */
  label: Int32Array;
  lakes: LakeInfo[];
}

/** FNV-1a 32 over a string → [0, 1) (the lake's turbidity hash). */
function hash01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d); h ^= h >>> 15; h = Math.imul(h, 0x846ca68b); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** A lake's turbidity: 0.85–1.25 by its level (stable across re-bakes), +0.1 for a pond (< 60 nodes ≈ 0.2 ha). */
export function lakeTurbidity(level: number, nodes: number): number {
  const k = 0.85 + 0.4 * hash01(`lake:${Math.round(level * 10)}`) + (nodes < 60 ? 0.1 : 0);
  return Math.min(1.35, Math.max(0.85, k));
}

/**
 * Label a baked lake region: 4-connected wet nodes at ONE level are one lake (a regional fill gives every node of a
 * component the same level; two components at different levels never merge, even where they touch).
 */
export function labelLakeRegion(region: LakeRegion): LakeLabels {
  const n = LAKE_GRID_N;
  const cell = LAKE_REGION_SIZE / (n - 1);
  const x0 = region.centerX - LAKE_REGION_SIZE / 2, z0 = region.centerZ - LAKE_REGION_SIZE / 2;
  const label = new Int32Array(n * n).fill(-1);
  const lakes: LakeInfo[] = [];
  const stack: number[] = [];
  for (let start = 0; start < n * n; start++) {
    const lv = region.surface[start];
    if (Number.isNaN(lv) || label[start] >= 0) continue;
    const index = lakes.length;
    let count = 0, sx = 0, sz = 0, bx0 = Infinity, bz0 = Infinity, bx1 = -Infinity, bz1 = -Infinity;
    label[start] = index; stack.push(start);
    while (stack.length) {
      const m = stack.pop()!;
      const r = (m / n) | 0, c = m - r * n;
      const wx = x0 + c * cell, wz = z0 + r * cell;
      count++; sx += wx; sz += wz;
      if (wx < bx0) bx0 = wx; if (wx > bx1) bx1 = wx; if (wz < bz0) bz0 = wz; if (wz > bz1) bz1 = wz;
      const nb = [c > 0 ? m - 1 : -1, c < n - 1 ? m + 1 : -1, r > 0 ? m - n : -1, r < n - 1 ? m + n : -1];
      for (const q of nb) {
        if (q < 0 || label[q] >= 0) continue;
        const s = region.surface[q];
        if (Number.isNaN(s) || Math.abs(s - lv) > 1e-3) continue;
        label[q] = index; stack.push(q);
      }
    }
    const cx = sx / count, cz = sz / count;
    lakes.push({
      index, level: lv, nodes: count, cx, cz, x0: bx0, z0: bz0, x1: bx1, z1: bz1,
      id: `lake:${Math.round(lv * 10)}:${Math.round(cx)}:${Math.round(cz)}`,
      turbidity: lakeTurbidity(lv, count),
    });
  }
  return { region, label, lakes };
}

/** The labelled lake under (x, z) (the nearest node, like `lakeSurfaceAtBaked`), or null. */
export function lakeLabelAt(labels: LakeLabels, x: number, z: number): LakeInfo | null {
  const n = LAKE_GRID_N;
  const col = Math.round(((x - labels.region.centerX) / LAKE_REGION_SIZE + 0.5) * (n - 1));
  const row = Math.round(((z - labels.region.centerZ) / LAKE_REGION_SIZE + 0.5) * (n - 1));
  if (col < 0 || col >= n || row < 0 || row >= n) return null;
  const l = labels.label[row * n + col];
  return l >= 0 ? labels.lakes[l] : null;
}

// ── Rivers: the network's segments near a point ─────────────────────────────

/**
 * The segment table's columns (MIRROR of @shared/terrain.ts `C_*` / `RIVER_COLS` / `RIVER_CELL` — not exported there;
 * verify:water-body pins the layout by decoding the packed segments against `riverWaterAt` on thousands of points).
 */
const C_AX = 0, C_AZ = 1, C_BX = 2, C_BZ = 3, C_WA = 4, C_WB = 5, C_DA = 6, C_DB = 7, C_PA = 8, C_PB = 9, C_FALL = 10;
const RIVER_COLS = 15;
const RIVER_CELL = 128;

export interface RiverSeg {
  /** Index into the network table. */
  index: number;
  ax: number; az: number; bx: number; bz: number;
  /** Water level at each end (m) and half width at each end (m). */
  wa: number; wb: number; hwa: number; hwb: number;
  /** Bed depth at each end (m, under the water). */
  da: number; db: number;
  /** Distance from the query point (m). */
  dist: number;
  /** A waterfall reach (only with `falls: true`). */
  fall?: boolean;
}

function cellRange(t: NonNullable<ReturnType<typeof riverNetworkTable>>, key: number): [number, number] | null {
  let lo = 0, hi = t.cellKeys.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const k = t.cellKeys[mid];
    if (k === key) return [t.cellStart[mid], t.cellStart[mid + 1]];
    if (k < key) lo = mid + 1; else hi = mid - 1;
  }
  return null;
}

/**
 * Every non-fall river segment within `radius` of (x, z), nearest first. A waterfall reach is left out: its water
 * holds the upstream level to the lip and drops over 4 m (the carve's shape), which a linear table cannot carry —
 * the plunge pool below is the next reach's, and the sheet itself hides the bed.
 */
export function riverSegmentsNear(seed: string, x: number, z: number, radius: number, opts: { falls?: boolean } = {}): RiverSeg[] {
  const t = riverNetworkTable(seed);
  if (!t) return [];
  const seen = new Set<number>();
  const out: RiverSeg[] = [];
  const c0x = Math.floor((x - radius) / RIVER_CELL), c1x = Math.floor((x + radius) / RIVER_CELL);
  const c0z = Math.floor((z - radius) / RIVER_CELL), c1z = Math.floor((z + radius) / RIVER_CELL);
  const s = t.seg;
  for (let ix = c0x; ix <= c1x; ix++) for (let iz = c0z; iz <= c1z; iz++) {
    const range = cellRange(t, (ix + 32768) * 65536 + (iz + 32768));
    if (!range) continue;
    for (let i = range[0]; i < range[1]; i++) {
      const si = t.cellSegs[i];
      if (seen.has(si)) continue;
      seen.add(si);
      const o = si * RIVER_COLS;
      if (s[o + C_FALL] !== 0 && !opts.falls) continue;
      const ax = s[o + C_AX], az = s[o + C_AZ], bx = s[o + C_BX], bz = s[o + C_BZ];
      const dx = bx - ax, dz = bz - az, len2 = dx * dx + dz * dz;
      let u = len2 > 0 ? ((x - ax) * dx + (z - az) * dz) / len2 : 0;
      u = Math.min(1, Math.max(0, u));
      const dist = Math.hypot(x - (ax + dx * u), z - (az + dz * u));
      if (dist > radius) continue;
      out.push({ index: si, ax, az, bx, bz, wa: s[o + C_WA], wb: s[o + C_WB], hwa: s[o + C_DA] * 0.5, hwb: s[o + C_DB] * 0.5, da: s[o + C_PA], db: s[o + C_PB], dist, fall: s[o + C_FALL] !== 0 });
    }
  }
  out.sort((p, q) => p.dist - q.dist || p.index - q.index);
  return out;
}


// ── bodyAt ──────────────────────────────────────────────────────────────────

let labelsCur: LakeLabels | null = null;
/** The labelled region the table was packed from (the lake descriptor's id and turbidity read it). */
export function setWaterBodyLabels(l: LakeLabels | null): void { labelsCur = l; }
export function waterBodyLabels(): LakeLabels | null { return labelsCur; }

/**
 * The body of water over (x, z), or null over dry ground. Order = the surface provider's `kindAt`: a baked lake, then a
 * river channel, then the sea (lakes sit ≥ 2 m above the sea by construction; a river crossing a lake is lake floor).
 */
export function bodyAt(x: number, z: number, src: WaterBodySources | null = sources): WaterBody | null {
  if (!src) return null;
  const lake = src.lakeAt(x, z);
  if (lake !== null) {
    const info = labelsCur ? lakeLabelAt(labelsCur, x, z) : null;
    const level = lake;
    return {
      kind: 'lake', id: info?.id ?? `lake:${Math.round(level * 10)}`, level, surfaceAt: () => level,
      optics: OPTICS_BY_BODY.lake, turbidity: info?.turbidity ?? lakeTurbidity(level, 1000), flow: { x: 0, z: 0, speed: 0 },
    };
  }
  const river = src.riverAt ? src.riverAt(x, z) : (src.seed ? riverWaterAt(src.seed, x, z) : null);
  if (river !== null) {
    const seg = src.seed ? riverSegmentsNear(src.seed, x, z, 40)[0] : undefined;
    let flow = { x: 0, z: 0, speed: 0 };
    let id = 'river';
    if (seg) {
      const len = Math.hypot(seg.bx - seg.ax, seg.bz - seg.az) || 1;
      flow = { x: (seg.bx - seg.ax) / len, z: (seg.bz - seg.az) / len, speed: manningSpeed((seg.da + seg.db) / 2, (seg.wa - seg.wb) / len) };
      id = `river:${seg.index}`;
    }
    return { kind: 'river', id, level: river, surfaceAt: () => river, optics: OPTICS_BY_BODY.river, turbidity: 1, flow };
  }
  if (src.seaAt(x, z)) {
    const surf = src.seaSurfaceAt ?? (() => src.seaLevel);
    return { kind: 'sea', id: 'sea', level: src.seaLevel, surfaceAt: surf, optics: OPTICS_BY_BODY.sea, turbidity: 1, flow: { x: 0, z: 0, speed: 0 } };
  }
  return null;
}

// ── The packed table the ground shader reads ────────────────────────────────

/**
 * The mask: a CAMERA-centred window of 12 m cells (2 lake nodes), 96 per side = 1,152 m (±576 m — past the river radius
 * and the view a submerged bed is seen in), aligned to the lake region's node lattice. Measured on three regions
 * (verify:water-body; 236 / 66 / 93 lakes): 24 m cells laid a false lake bed on 0.2–0.6 % of the dry ground ≥ 12 m from
 * any lake — low ground inside a cell a lake touches, up to 4 m "deep" — and a one-node dilation tripled it; 12 m cells
 * with no dilation laid none. The whole region at 12 m would be 16 KB, the WebGL2 minimum block size.
 */
export const BODY_GRID = 96;
export const NODES_PER_CELL = 2;
export const BODY_CELL_M = NODES_PER_CELL * (LAKE_REGION_SIZE / (LAKE_GRID_N - 1));
export const BODY_WINDOW_M = BODY_GRID * BODY_CELL_M;
/** Lake slots (6-bit ids 1..62; 0 = none, 63 = "a river runs through this cell"). A region holds hundreds of ponds
 *  (236 around the user's lake): the nearest 62 take slots. */
export const LAKE_SLOTS = 62;
export const RIVER_CELL_ID = 63;
/** Bits per mask id (four ids to a float: 24 bits — the largest integer float32 holds exactly is 2²⁴). */
export const MASK_BITS = 6;
const ID_BASE = 1 << MASK_BITS;
/** River segments in the table (the nearest), and the radius they are gathered in. */
export const RIVER_SEGS = 32;
export const RIVER_RADIUS_M = 320;
/** Layout (vec4 elements): [0] header (cell m, grid n, river count, lake count), [1] spare, [2 + id] lake slot
 *  (level, turbidity), then the river segments (2 each: (ax, az, bx, bz) and (wa, wb, hwa, hwb), pack-origin relative),
 *  then the mask. */
export const SLOT_BASE = 2;
export const RIVER_BASE = SLOT_BASE + ID_BASE;
export const MASK_BASE = RIVER_BASE + 2 * RIVER_SEGS;
export const MASK_VEC4 = (BODY_GRID * BODY_GRID) / 16;
export const TABLE_VEC4 = MASK_BASE + MASK_VEC4;

export interface BodyTable {
  /** Exactly what the shader's uniform array holds (TABLE_VEC4 × 4 floats). */
  data: Float32Array;
  /** The pack origin (absolute m): the mask grid's corner; every packed x/z is relative to it. */
  originX: number; originZ: number;
  lakeCount: number; riverCount: number;
  slots: Array<{ id: string; level: number; turbidity: number } | null>;
  rivers: RiverSeg[];
  /** Cells marked as lake / river (probe). */
  lakeCells: number; riverCells: number;
  /** The camera point the rivers were gathered around. */
  atX: number; atZ: number;
}

/** 6-bit ids, four to a float (24 bits — exact in float32), four floats to a vec4: 16 cells per element. */
function packMask(ids: Uint8Array, data: Float32Array): void {
  for (let v = 0; v < MASK_VEC4; v++) {
    for (let comp = 0; comp < 4; comp++) {
      let f = 0;
      for (let slot = 0; slot < 4; slot++) f += ids[v * 16 + comp * 4 + slot] * ID_BASE ** slot;
      data[(MASK_BASE + v) * 4 + comp] = f;
    }
  }
}

/**
 * Pack the lakes of a labelled region and the rivers near (camX, camZ) into the camera-centred window (aligned to the
 * region's node lattice; with no region, to a 12 m lattice). Lakes take slots nearest the camera first (≤ 62); a mask
 * cell carries the lake id of the most of its nodes (the cell's own 3 × 3 — its corners and edge midpoints; no
 * dilation), a river cell id 63 where a packed channel passes within the cell's half-diagonal of its centre. Where two
 * lakes share a cell, the one with more nodes there takes it (the other's few nodes in that cell shade against its level).
 */
export function packBodyTable(labels: LakeLabels | null, rivers: RiverSeg[], camX: number, camZ: number): BodyTable {
  const data = new Float32Array(TABLE_VEC4 * 4);
  const n = LAKE_GRID_N;
  const lat0X = labels ? labels.region.centerX - LAKE_REGION_SIZE / 2 : 0;
  const lat0Z = labels ? labels.region.centerZ - LAKE_REGION_SIZE / 2 : 0;
  // The window's first cell on the lattice (in cells from the lattice origin).
  const offX = Math.round((camX - BODY_WINDOW_M / 2 - lat0X) / BODY_CELL_M);
  const offZ = Math.round((camZ - BODY_WINDOW_M / 2 - lat0Z) / BODY_CELL_M);
  const originX = lat0X + offX * BODY_CELL_M, originZ = lat0Z + offZ * BODY_CELL_M;
  const ids = new Uint8Array(BODY_GRID * BODY_GRID);
  const slots: BodyTable['slots'] = new Array(ID_BASE).fill(null);
  const slotOf = new Int32Array(labels ? labels.lakes.length : 0).fill(0);
  let lakeCells = 0, riverCells = 0;
  if (labels && labels.lakes.length) {
    const ranked = labels.lakes.map((l) => ({ l, d: Math.hypot(Math.max(l.x0 - camX, 0, camX - l.x1), Math.max(l.z0 - camZ, 0, camZ - l.z1)) }))
      .sort((a, b) => a.d - b.d || b.l.nodes - a.l.nodes || a.l.index - b.l.index);
    ranked.slice(0, LAKE_SLOTS).forEach(({ l }, i) => {
      const id = i + 1;
      slotOf[l.index] = id;
      slots[id] = { id: l.id, level: l.level, turbidity: l.turbidity };
    });
    const count = new Map<number, number>();
    for (let cj = 0; cj < BODY_GRID; cj++) {
      for (let ci = 0; ci < BODY_GRID; ci++) {
        count.clear();
        const r0 = (offZ + cj) * NODES_PER_CELL, c0 = (offX + ci) * NODES_PER_CELL;
        if (r0 + NODES_PER_CELL < 0 || r0 > n - 1 || c0 + NODES_PER_CELL < 0 || c0 > n - 1) continue; // outside the baked region
        for (let r = Math.max(0, r0); r <= Math.min(n - 1, r0 + NODES_PER_CELL); r++) {
          for (let c = Math.max(0, c0); c <= Math.min(n - 1, c0 + NODES_PER_CELL); c++) {
            const lb = labels.label[r * n + c];
            if (lb < 0 || slotOf[lb] === 0) continue;
            count.set(slotOf[lb], (count.get(slotOf[lb]) ?? 0) + 1);
          }
        }
        let best = 0, bestN = 0;
        for (const [id, k] of count) if (k > bestN || (k === bestN && id < best)) { best = id; bestN = k; }
        if (best) { ids[cj * BODY_GRID + ci] = best; lakeCells++; }
      }
    }
  }
  const packed = rivers.slice(0, RIVER_SEGS);
  const half = BODY_CELL_M * Math.SQRT1_2;
  packed.forEach((s, i) => {
    const o = (RIVER_BASE + 2 * i) * 4;
    data[o] = s.ax - originX; data[o + 1] = s.az - originZ; data[o + 2] = s.bx - originX; data[o + 3] = s.bz - originZ;
    data[o + 4] = s.wa; data[o + 5] = s.wb; data[o + 6] = s.hwa; data[o + 7] = s.hwb;
    const reach = Math.max(s.hwa, s.hwb) + half + 1;
    const lx0 = Math.min(s.ax, s.bx) - originX - reach, lx1 = Math.max(s.ax, s.bx) - originX + reach;
    const lz0 = Math.min(s.az, s.bz) - originZ - reach, lz1 = Math.max(s.az, s.bz) - originZ + reach;
    for (let cj = Math.max(0, Math.floor(lz0 / BODY_CELL_M)); cj <= Math.min(BODY_GRID - 1, Math.floor(lz1 / BODY_CELL_M)); cj++) {
      for (let ci = Math.max(0, Math.floor(lx0 / BODY_CELL_M)); ci <= Math.min(BODY_GRID - 1, Math.floor(lx1 / BODY_CELL_M)); ci++) {
        const k = cj * BODY_GRID + ci;
        if (ids[k] !== 0) continue;
        const px = originX + (ci + 0.5) * BODY_CELL_M, pz = originZ + (cj + 0.5) * BODY_CELL_M;
        const dx = s.bx - s.ax, dz = s.bz - s.az, len2 = dx * dx + dz * dz;
        let u = len2 > 0 ? ((px - s.ax) * dx + (pz - s.az) * dz) / len2 : 0;
        u = Math.min(1, Math.max(0, u));
        if (Math.hypot(px - (s.ax + dx * u), pz - (s.az + dz * u)) <= reach) { ids[k] = RIVER_CELL_ID; riverCells++; }
      }
    }
  });
  packMask(ids, data);
  for (let id = 1; id <= LAKE_SLOTS; id++) {
    const sl = slots[id];
    if (!sl) continue;
    data[(SLOT_BASE + id) * 4] = sl.level;
    data[(SLOT_BASE + id) * 4 + 1] = sl.turbidity;
  }
  const lakeCount = slots.filter(Boolean).length;
  data[0] = BODY_CELL_M; data[1] = BODY_GRID; data[2] = packed.length; data[3] = lakeCount;
  return { data, originX, originZ, lakeCount, riverCount: packed.length, slots, rivers: packed, lakeCells, riverCells, atX: camX, atZ: camZ };
}

export interface BodyDecode {
  kind: 'lake' | 'river' | null;
  /** The mask id (0 none, 1..62 a lake slot, 63 a river cell). */
  id: number;
  /** The water level the shader shades against, and the depth under it (m; ≤ 0.02 = no shading). */
  level: number;
  depth: number;
  turbidity: number;
}

const f32 = Math.fround;

/**
 * The GROUND SHADER's decode, on the CPU (float32 where the GPU is): the mask id of the cell under (x, z), the lake
 * slot's level, or the nearest packed river channel covering the point. `y` = the ground height. THE ORACLE the
 * harness compares against `lakeSurfaceAtBaked` / `riverWaterAt`.
 */
export function decodeBodyTable(t: BodyTable, x: number, y: number, z: number): BodyDecode {
  const d = t.data;
  const none: BodyDecode = { kind: null, id: 0, level: NaN, depth: 0, turbidity: 1 };
  const lx = f32(x - t.originX), lz = f32(z - t.originZ);
  const cell = d[0], gn = d[1];
  const col = Math.floor(f32(lx / cell)), row = Math.floor(f32(lz / cell));
  if (col < 0 || col >= gn || row < 0 || row >= gn) return none;
  const ci = row * gn + col;
  const vecIdx = Math.floor(ci / 16), comp = Math.floor(ci / 4) % 4, slot = ci % 4;
  const fl = d[(MASK_BASE + vecIdx) * 4 + comp];
  // × the power-of-two RECIPROCAL, as the shader does: a GPU division may be off by ULPs, a power-of-two product is exact.
  const inv = [1, 1 / ID_BASE, 1 / ID_BASE ** 2, 1 / ID_BASE ** 3][slot];
  const id = Math.floor(fl * inv) - ID_BASE * Math.floor(fl * (inv / ID_BASE));
  if (id >= 1 && id <= LAKE_SLOTS) {
    const level = d[(SLOT_BASE + id) * 4];
    return { kind: 'lake', id, level, depth: f32(level - y), turbidity: d[(SLOT_BASE + id) * 4 + 1] };
  }
  if (id !== RIVER_CELL_ID) return { ...none, id };
  let best = 1e9, water = NaN;
  for (let i = 0; i < d[2]; i++) {
    const o = (RIVER_BASE + 2 * i) * 4;
    const ax = d[o], az = d[o + 1], bx = d[o + 2], bz = d[o + 3];
    const dx = f32(bx - ax), dz = f32(bz - az);
    const len2 = Math.max(f32(dx * dx + dz * dz), 1e-6);
    const tt = Math.min(1, Math.max(0, f32(f32((lx - ax) * dx + (lz - az) * dz) / len2)));
    const ex = f32(lx - (ax + dx * tt)), ez = f32(lz - (az + dz * tt));
    const dist = Math.sqrt(ex * ex + ez * ez);
    const hw = d[o + 6] + (d[o + 7] - d[o + 6]) * tt;
    if (dist < hw && dist < best) { best = dist; water = f32(d[o + 4] + (d[o + 5] - d[o + 4]) * tt); }
  }
  if (Number.isNaN(water)) return { ...none, id };
  return { kind: 'river', id, level: water, depth: f32(water - y), turbidity: 1 };
}

// ── The waterline: a camera → the engine's near-plane line ─────────────────

/** A camera as the waterline needs it (a THREE.PerspectiveCamera satisfies it). */
export interface WaterlineCamera {
  matrixWorld: { elements: ArrayLike<number> };
  near: number; fov: number; aspect: number;
  /** Optional: three's zoom divides the tangent. */
  zoom?: number;
}

/**
 * The near-plane waterline for `camera` at ABSOLUTE position `camAbs` over a surface at `surfaceY` with slope
 * (dh/dx, dh/dz): the camera's basis from its world matrix (render space rotates like absolute space — the rebase is
 * a translation), the plane's normal from the slope.
 */
export function waterlineFor(camera: WaterlineCamera, camAbs: { x: number; y: number; z: number }, surfaceY: number, slopeX = 0, slopeZ = 0): NearPlaneWaterline {
  const e = camera.matrixWorld.elements;
  const nr = (a: number, b: number, c: number): [number, number, number] => { const l = Math.hypot(a, b, c) || 1; return [a / l, b / l, c / l]; };
  const right = nr(e[0], e[1], e[2]), up = nr(e[4], e[5], e[6]), forward = nr(-e[8], -e[9], -e[10]);
  const tanHalf = Math.tan(((camera.fov / 2) * Math.PI) / 180) / Math.max(1e-6, camera.zoom ?? 1);
  return nearPlaneWaterline({
    cam: [camAbs.x, camAbs.y, camAbs.z], right, up, forward,
    near: camera.near, tanHalfFovY: tanHalf, aspect: camera.aspect,
    surfaceY, normal: nr(-slopeX, 1, -slopeZ),
  });
}

// ── The switch and the lever state ──────────────────────────────────────────

let seaFixCache = true;
let seaFixAt = -1e9;
/**
 * The sea bed's CORRECTED mix is the default (coordinator, 2026-09-29 — THE MIX LAW: the shipped `col.mix(lit, inWater)`
 * blended by the ALBEDO, so the sea floor paled toward white as it deepened; the bed pictures: pale 161/184/134 vs the
 * intended teal-dark 74/145/99 at 3 m). `localStorage 'atlas:sea-bed-fix' = 'off'` restores the exact 2026-09-25 look.
 */
export function seaBedFixEnabled(nowMs: number = (typeof performance !== 'undefined' ? performance.now() : Date.now())): boolean {
  if (nowMs - seaFixAt < 1000) return seaFixCache;
  seaFixAt = nowMs;
  try { seaFixCache = (globalThis as { localStorage?: Storage }).localStorage?.getItem('atlas:sea-bed-fix') !== 'off'; } catch { seaFixCache = true; }
  return seaFixCache;
}

let enabledCache = true;
let enabledAt = -1e9;
/** `localStorage 'atlas:water-bodies' = 'off'` falls back to the sea-only behaviour (read at most once a second). */
export function waterBodiesEnabled(nowMs: number = (typeof performance !== 'undefined' ? performance.now() : Date.now())): boolean {
  if (nowMs - enabledAt < 1000) return enabledCache;
  enabledAt = nowMs;
  try { enabledCache = (globalThis as { localStorage?: Storage }).localStorage?.getItem('atlas:water-bodies') !== 'off'; } catch { enabledCache = true; }
  return enabledCache;
}
